import { useSdkContext } from "scmp-sdk";

import { RouteType } from "./enum";

export type Section = {
  label: string;
  labelLink?: string;
  links: SitemapItemLink[];
};

export type SitemapItemLink = {
  customQueryParameters?: Record<string, string>;
  isExternal?: boolean;
  label: string;
  url: {
    alias: string;
    applicationId?: string;
    entityId?: string;
    entityUuid?: string;
    type?: RouteType;
  };
};

type Sitemap = {
  sections: Section[];
};

export const useSitemap = () => {
  const { config } = useSdkContext();

  const sitemap: Sitemap = {
    sections: [
      {
        label: "Company",
        labelLink: "https://corp.scmp.com/",
        links: [
          {
            isExternal: true,
            label: "About SCMP",
            url: {
              alias: "https://corp.scmp.com/our-company",
              applicationId: "about-scmp",
            },
          },
          {
            label: "Policies & Standards",
            url: {
              alias: "/policies-and-standards",
              entityId: "3074375",
              entityUuid: "dd1bc258-b7b9-437c-bc4c-99130d8a5a3f",
              type: RouteType.Page,
            },
          },
          {
            label: "Privacy Policy",
            url: {
              alias: "/privacy-policy",
              entityId: "800172",
              entityUuid: "d1c18b93-cb6c-48a2-bf84-7c2acb96d4cc",
              type: RouteType.Page,
            },
          },
          {
            label: "Terms & Conditions",
            url: {
              alias: "/terms-conditions",
              entityId: "800173",
              entityUuid: "c18b91b9-6e24-45dc-ba05-974848666e4b",
              type: RouteType.Page,
            },
          },
          {
            isExternal: true,
            label: "Careers with SCMP",
            url: {
              alias: "https://corp.scmp.com/work-with-us/",
              applicationId: "work-for-us",
            },
          },
          {
            isExternal: true,
            label: "Help Centre",
            url: {
              alias: "https://help.scmp.com/hc",
              applicationId: "help-centre",
            },
          },
          {
            label: "Contact Us",
            url: {
              alias: "/contact-us",
              entityId: "800171",
              entityUuid: "b580e1d5-6698-42df-a1c2-1f7bdb2a0a04",
              type: RouteType.Page,
            },
          },
        ],
      },
      {
        label: "Services",
        labelLink: "https://corp.scmp.com/our-services",
        links: [
          {
            isExternal: true,
            label: "Digital Subscriptions",
            url: {
              alias: config.subscribe.host,
            },
          },
          {
            isExternal: true,
            label: "Print + Subscriptions",
            url: {
              alias: `${config.subscribe.host}/bundle`,
            },
          },
          {
            isExternal: true,
            label: "Group Subscriptions",
            url: {
              alias: "https://enterprise.scmp.com/news-and-intelligence",
              applicationId: "group-subscriptions",
            },
          },
          {
            isExternal: true,
            label: "Syndication & Licensing",
            url: {
              alias: "https://enterprise.scmp.com/licensing",
            },
          },
          {
            isExternal: true,
            label: "Advertising+",
            url: {
              alias: "http://advertising.scmp.com/",
              applicationId: "advertising-with-us",
            },
          },
          {
            isExternal: true,
            label: "Enterprise+",
            url: {
              alias: "https://enterprise.scmp.com/",
              applicationId: "enterprise",
            },
          },
          {
            isExternal: true,
            label: "Learn",
            url: {
              alias: "https://www.scmplearn.com/",
              applicationId: "scmp-learn",
            },
          },
          {
            isExternal: true,
            label: "SCMP Live",
            url: {
              alias: "https://live.scmp.com",
              applicationId: "scmp-live",
            },
          },
        ],
      },
      {
        label: "SCMP.com",
        labelLink: "https://www.scmp.com/",
        links: [
          {
            label: "Latest",
            url: {
              alias: "/live",
            },
          },
          {
            label: "Hong Kong",
            url: {
              alias: "/news/hong-kong",
            },
          },
          {
            label: "China",
            url: {
              alias: "/news/china",
            },
          },
          {
            label: "Asia",
            url: {
              alias: "/news/asia",
            },
          },
          {
            label: "World",
            url: {
              alias: "/news/world",
            },
          },
          {
            label: "Business",
            url: {
              alias: "/business",
            },
          },
          {
            label: "Economy",
            url: {
              alias: "/economy",
            },
          },
          {
            label: "Tech",
            url: {
              alias: "/tech",
            },
          },
          {
            label: "Lifestyle",
            url: {
              alias: "/lifestyle",
            },
          },
          {
            label: "People & Culture",
            url: {
              alias: "/news/people-culture",
            },
          },
          {
            label: "Opinion",
            url: {
              alias: "/comment",
            },
          },
          {
            label: "Sport",
            url: {
              alias: "/sport",
            },
          },
          {
            label: "China Future Tech",
            url: {
              alias: "/news/china-future-tech",
            },
          },
          {
            isExternal: true,
            label: "China Power Players",
            url: {
              alias:
                "https://multimedia.scmp.com/infographics/news/china/article/3205322/china-economy-decision-makers/",
              applicationId: "china-power-players",
            },
          },
          {
            label: "This Week In Asia",
            url: {
              alias: "/week-asia",
            },
          },
          {
            label: "Open Questions",
            url: {
              alias: "/news/china/series/3256715/open-questions",
            },
          },
        ],
      },
      {
        label: "More",
        links: [
          {
            label: "Video",
            url: {
              alias: "/video",
            },
          },
          {
            label: "Pictures",
            url: {
              alias: "/photos",
            },
          },
          {
            label: "Infographics",
            url: {
              alias: "/infographic",
            },
          },
          {
            label: "Games",
            url: {
              alias: "/games",
            },
          },
          {
            label: "SCMP Series",
            url: {
              alias: "/series",
            },
          },
          {
            label: "Spotlight",
            url: {
              alias: "/scmp-spotlight",
            },
          },
          {
            label: "All Topics",
            url: {
              alias: "/topics",
            },
          },
          {
            label: "All Authors",
            url: {
              alias: "/authors",
            },
          },
          {
            label: "RSS Feeds",
            url: {
              alias: "/rss",
            },
          },
        ],
      },
      {
        label: "Other Sites",
        links: [
          {
            isExternal: true,
            label: "Cosmopolitan",
            url: {
              alias: "https://www.cosmopolitan.com.hk",
              applicationId: "cosmopolitan",
            },
          },
          {
            isExternal: true,
            label: "Elle",
            url: {
              alias: "https://www.elle.com.hk",
              applicationId: "elle",
            },
          },
          {
            isExternal: true,
            label: "Esquire",
            url: {
              alias: "https://www.esquirehk.com",
              applicationId: "esquire",
            },
          },
          {
            isExternal: true,
            label: "Harper's BAZAAR",
            url: {
              alias: "https://www.harpersbazaar.com.hk",
              applicationId: "harpers-bazaar",
            },
          },
          {
            isExternal: true,
            label: "CosMart" /*  Cspell:ignore Cosmart */,
            url: {
              alias: "https://www.cosmart.hk",
              applicationId: "cosmart",
            },
          },
          {
            label: "Operation Santa Claus",
            url: {
              alias: "https://osc.scmp.com/",
              applicationId: "operation-santa-claus",
            },
          },
        ],
      },
      {
        label: "Products",
        links: [
          {
            isExternal: true,
            label: "Mobile & apps",
            url: {
              alias: "https://app.scmp.com/",
              applicationId: "mobile-and-app",
            },
          },
          {
            isExternal: true,
            label: "ePaper",
            url: {
              alias: "/epaper",
            },
          },
          {
            label: "SCMP Plus",
            url: {
              alias: "/plus",
            },
          },
          {
            label: "Style",
            url: {
              alias: "/magazines/style",
            },
          },
          {
            label: "PostMag",
            url: {
              alias: "/postmag",
            },
          },
          {
            isExternal: true,
            label: "100 Top Tables",
            url: {
              alias: "https://100toptables.scmp.com/",
              applicationId: "100-top-tables",
            },
          },
          {
            label: "HK Racing",
            url: {
              alias: "/sport/racing",
            },
          },
          {
            isExternal: true,
            label: "Young Post",
            url: {
              alias: "https://www.youngpostclub.com/yp",
              applicationId: "yp",
            },
          },
          {
            isExternal: true,
            label: "Young Post Spark",
            url: {
              alias: "https://www.youngpostclub.com/spark",
              applicationId: "spark",
            },
          },
          {
            isExternal: true,
            label: "Posties",
            url: {
              alias: "https://www.youngpostclub.com/posties",
              applicationId: "posties",
            },
          },
          {
            isExternal: true,
            label: "Good Schools Guide",
            url: {
              alias: "https://goodschoolsguide.scmp.com/",
            },
          },
          {
            label: "Cooking",
            url: {
              alias: "/cooking",
            },
          },
          {
            isExternal: true,
            label: "The Store",
            url: {
              alias: "https://store.scmp.com/",
            },
          },
        ],
      },
      {
        label: "Account",
        links: [
          {
            isExternal: true,
            label: "My Account",
            url: {
              alias: "https://account.scmp.com/",
              applicationId: "my-account",
            },
          },
          {
            label: "Newsletters & Alerts",
            url: {
              alias: "/newsletters",
            },
          },
          {
            label: "myNews",
            url: {
              alias: "/mynews",
            },
          },
        ],
      },
    ],
  };

  return { sitemap };
};
